// WHG Docs signs a person out after an hour without activity, and keeps
// the sign-in alive for as long as they keep working (Matthew, 2026-10-28).
//
// Activity means the person clicking, typing, scrolling or moving the
// mouse in any WHG Docs tab. Background polling never counts, or an open
// tab would never sign out. The clock lives in localStorage so every tab
// shares it: working in one tab keeps the others signed in, and signing
// out in one signs out all of them.

import Cookies from "js-cookie";

export const IDLE_LIMIT_MS = 60 * 60 * 1000;
export const WARNING_AT_MS = 55 * 60 * 1000;
export const RENEW_EVERY_MS = 5 * 60 * 1000;
const ACTIVITY_WRITE_EVERY_MS = 5 * 1000;

export const LAST_ACTIVITY_KEY = "whgdocs.lastActivityAt";
export const LAST_RENEWAL_KEY = "whgdocs.lastRenewalAt";
export const SIGN_IN_KEY = "whgdocs.activitySignIn";
export const SESSION_ENDED_KEY = "whgdocs.sessionEnded";

let keeperRunning = false;

export function setKeeperRunning(running) {
  keeperRunning = running;
}

function readNumber(key) {
  try {
    const value = Number(localStorage.getItem(key));
    return Number.isFinite(value) ? value : 0;
  } catch (error) {
    return 0;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, String(value));
  } catch (error) {
    // Storage unavailable: the server's own idle hour still applies.
  }
}

export const lastActivityAt = () => readNumber(LAST_ACTIVITY_KEY);
export const lastRenewalAt = () => readNumber(LAST_RENEWAL_KEY);
export const setLastRenewalAt = (at) => write(LAST_RENEWAL_KEY, at);

export function idleFor(now = Date.now()) {
  const last = lastActivityAt();
  return last ? Math.max(0, now - last) : 0;
}

/** True once the idle hour has passed in a tab that is enforcing it. */
export function isIdleExpired(now = Date.now()) {
  return keeperRunning && hasSignIn() && idleFor(now) >= IDLE_LIMIT_MS;
}

/** Count activity now, unless the idle hour has already run out. */
export function recordActivity(now = Date.now(), { force = false } = {}) {
  if (idleFor(now) >= IDLE_LIMIT_MS) return false;
  if (force || now - lastActivityAt() >= ACTIVITY_WRITE_EVERY_MS) {
    write(LAST_ACTIVITY_KEY, now);
  }
  return true;
}

// The sign-in the shared clock belongs to: the server's sid for browser
// sign-ins, else the token id. A fresh sign-in restarts the clock, so a
// stale timestamp from yesterday never signs someone straight back out.
function currentSignIn() {
  const sid = Cookies.get("signin_sid");
  return sid || null;
}

function knownSignIn() {
  try {
    return localStorage.getItem(SIGN_IN_KEY);
  } catch (error) {
    return null;
  }
}

// True while this device holds a sign-in, or held one that lapsed without
// being signed out (the browser drops the cookie when the idle hour is up,
// and that must still end in a real sign-out).
export const hasSignIn = () => currentSignIn() !== null || Boolean(knownSignIn());

/** Called on every sign-out, so a later visit starts the normal sign-in. */
export function forgetSignIn() {
  try {
    localStorage.removeItem(SIGN_IN_KEY);
  } catch (error) {
    // nothing to forget
  }
}

export function syncSignIn(now = Date.now()) {
  const signIn = currentSignIn();
  let known = null;
  try {
    known = localStorage.getItem(SIGN_IN_KEY);
  } catch (error) {
    return;
  }
  if (signIn && signIn !== known) {
    write(SIGN_IN_KEY, signIn);
    write(LAST_ACTIVITY_KEY, now);
    write(LAST_RENEWAL_KEY, now);
  } else if (!lastActivityAt()) {
    write(LAST_ACTIVITY_KEY, now);
  }
}

/** Start the clock over for a sign-in the person is about to make. */
export function startFresh(now = Date.now()) {
  clearSessionEnded();
  write(LAST_ACTIVITY_KEY, now);
  write(LAST_RENEWAL_KEY, now);
}

/** "idle" or "ended" after WHG Docs signed the person out, else null. */
export function sessionEndedReason() {
  try {
    const raw = localStorage.getItem(SESSION_ENDED_KEY);
    return raw ? JSON.parse(raw).reason || "ended" : null;
  } catch (error) {
    return null;
  }
}

export function markSessionEnded(reason, now = Date.now()) {
  write(SESSION_ENDED_KEY, JSON.stringify({ reason, at: now }));
}

export function clearSessionEnded() {
  try {
    localStorage.removeItem(SESSION_ENDED_KEY);
  } catch (error) {
    // nothing to clear
  }
}
